import React, { useEffect, useRef, useState } from "react";
import { Box } from "@mui/material";
import { EMPTY_CHARACTER } from "@pulse/shared-components";
import { GetCustomPlaceholdersRPC } from "@pulse/pulse-rpcs/lib/email-cycle/getCustomPlaceholdersRPC";
import {
  AutoCompleteInputField,
  AutoCompleteItem,
  Snackbar,
  SnackbarProps,
} from "@surya-digital/leo-reactjs-material-ui";
import { useTranslation } from "react-i18next";
import { observer } from "mobx-react";
import { UnexpectedErrorDialog } from "../../../../components/dialogs/UnexpectedErrorDialog";
import { useCustomPlaceholdersStore } from "../../stores/Hooks";

interface CampaignEmailPlaceholderPickerProps {
  projectId: string | null;
  disabled?: boolean;
  onInvalidProjectId?: VoidFunction;
}

export const CampaignEmailPlaceholderPicker: React.FC<CampaignEmailPlaceholderPickerProps> =
  observer(({ projectId, disabled = false, onInvalidProjectId }) => {
    const [snackbarConfig, setSnackbarConfig] =
      useState<Pick<SnackbarProps, "message" | "severity">>();
    const [
      copyPlaceholderUnexpectedErrorMessage,
      setCopyPlaceholderUnexpectedErrorMessage,
    ] = useState<string>();
    const isCustomPlaceholderFieldCleared = useRef(false);
    const [customPlaceholderSearchText, setCustomPlaceholderSearchText] =
      useState(EMPTY_CHARACTER);

    const { t } = useTranslation();
    const customPlaceholdersStore = useCustomPlaceholdersStore();

    useEffect(() => {
      isCustomPlaceholderFieldCleared.current = false;
      setCustomPlaceholderSearchText(EMPTY_CHARACTER);
      customPlaceholdersStore.setProjectId(projectId);
    }, [customPlaceholdersStore, projectId]);

    useEffect(() => {
      if (
        customPlaceholdersStore.getCustomPlaceholdersRPCError ===
        GetCustomPlaceholdersRPC.RPCError.InvalidProjectId
      ) {
        onInvalidProjectId?.();
      }
    }, [
      customPlaceholdersStore.getCustomPlaceholdersRPCError,
      onInvalidProjectId,
    ]);

    const customPlaceholderOptions: AutoCompleteItem[] =
      customPlaceholdersStore.customPlaceholders.map(
        ({ displayName, placeholder }) => ({
          label: displayName,
          id: placeholder,
        }),
      );

    const ensureCustomPlaceholdersFetched = async (): Promise<void> => {
      if (disabled) return;

      await customPlaceholdersStore.fetchCustomPlaceholders(projectId);
    };

    const handlePlaceholderCopy = async (
      value: AutoCompleteItem | null,
    ): Promise<void> => {
      if (!value?.id) return;

      try {
        await navigator.clipboard.writeText(`{{${value.id}}}`);
        isCustomPlaceholderFieldCleared.current = true;
        setCustomPlaceholderSearchText(EMPTY_CHARACTER);
        setSnackbarConfig({
          message: t("emailCycles.placeholderCopiedToClipboardSuccessMessage"),
          severity: "success",
        });
      } catch {
        setCopyPlaceholderUnexpectedErrorMessage(
          t("emailCycles.copyPlaceholderUnexpectedErrorMessage"),
        );
      }
    };

    const handleCustomPlaceholderInputChange = (
      inputText: string | null,
    ): void => {
      void ensureCustomPlaceholdersFetched();

      if (isCustomPlaceholderFieldCleared.current) {
        isCustomPlaceholderFieldCleared.current = false;
        setCustomPlaceholderSearchText(EMPTY_CHARACTER);
        return;
      }

      setCustomPlaceholderSearchText(inputText ?? EMPTY_CHARACTER);
    };

    return (
      <>
        {snackbarConfig && (
          <Snackbar
            onClose={() => setSnackbarConfig(undefined)}
            isDismissible
            isOpen
            {...snackbarConfig}
          />
        )}

        <Box
          onFocusCapture={() => {
            void ensureCustomPlaceholdersFetched();
          }}
          onMouseDownCapture={() => {
            void ensureCustomPlaceholdersFetched();
          }}
        >
          <AutoCompleteInputField
            inputValue={customPlaceholderSearchText}
            label={t("emailCycles.copyPlaceholder")}
            options={customPlaceholderOptions}
            value={null}
            onChange={handlePlaceholderCopy}
            onInputChange={handleCustomPlaceholderInputChange}
            placeholder={t("emailCycles.copyPlaceholderPlaceholder")}
            loading={customPlaceholdersStore.loadingCustomPlaceholders}
            loadingText={t("emailCycles.loadingCustomPlaceholdersText")}
            noOptionsText={t("emailCycles.customPlaceholdersNoOptionsText")}
            disabled={disabled}
          />
        </Box>

        {customPlaceholdersStore.hasUnexpectedGetCustomPlaceholdersError && (
          <UnexpectedErrorDialog
            message={t(
              "emailCycles.getCustomPlaceholdersUnexpectedErrorMessage",
            )}
            onClickDone={() =>
              customPlaceholdersStore.clearUnexpectedGetCustomPlaceholdersError()
            }
          />
        )}

        {copyPlaceholderUnexpectedErrorMessage && (
          <UnexpectedErrorDialog
            message={copyPlaceholderUnexpectedErrorMessage}
            onClickDone={() =>
              setCopyPlaceholderUnexpectedErrorMessage(undefined)
            }
          />
        )}
      </>
    );
  });
